if (!customElements.get('desktop-sticky-atc-handler')) {
  customElements.define(
    'desktop-sticky-atc-handler',
    class DesktopStickyAtcHandler extends HTMLElement {
      connectedCallback() {
        this.sticky = document.querySelector('[data-desktop-sticky-atc]');
        if (!this.sticky) return;

        this.productJsonEl = document.querySelector('[data-desktop-sticky-product-json]');
        if (!this.productJsonEl) return;

        try {
          this.productData = JSON.parse(this.productJsonEl.textContent);
        } catch (error) {
          console.error('Sticky ATC product JSON parse error', error);
          return;
        }

        this.image = this.sticky.querySelector('[data-sticky-image]');
        this.prodName = this.sticky.querySelector('[data-sticky-title]');
        this.button = this.sticky.querySelector('[data-desktop-sticky-atc-button]');
        this.currentPrice = this.sticky.querySelector('[data-sticky-current-price]');
        this.comparePrice = this.sticky.querySelector('[data-sticky-compare-price]');
        this.discount = this.sticky.querySelector('[data-sticky-discount]');
        this.availability = this.sticky.querySelector('[data-sticky-availability]');
        this.lowStockThreshold = Number(this.sticky.dataset.lowStockThreshold || 3);

        this.onButtonClick = this.onButtonClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onTriggerIntersect = this.onTriggerIntersect.bind(this);

        this.button?.addEventListener('click', this.onButtonClick);

        document.addEventListener('variant:change', this.onVariantChange);
        document.addEventListener('change', this.onVariantChange);

        window.addEventListener('resize', this.onResize);

        this.observeVariantIdInput();
        this.syncFromSelectedVariant();

        this.setTriggerElement();
        this.observeTriggerElement();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onButtonClick);
        document.removeEventListener('variant:change', this.onVariantChange);
        document.removeEventListener('change', this.onVariantChange);
        window.removeEventListener('resize', this.onResize);
        this.variantObserver?.disconnect();
        this.triggerObserver?.disconnect();
      }

      setTriggerElement() {
        this.triggerElement =
          document.querySelector('[data-product-info-container]') ||
          document.querySelector('.product') ||
          document.querySelector('.product__info-container') ||
          document.querySelector('main .shopify-section');
      }

      /* PERF: was a scroll-event listener recomputing a pixel triggerPoint
         (getBoundingClientRect - a layout-forcing read) and comparing it against scrollTop on
         every scroll frame. IntersectionObserver only fires when the trigger element's
         visibility actually crosses the viewport edge, matching the pattern this file's own
         sibling sticky-ATC implementations (assets/sticky-add-to-cart.js, the mobile bar driven
         from assets/product-details.js) already use. */
      observeTriggerElement() {
        if (!this.triggerElement || !('IntersectionObserver' in window)) {
          // No trigger element, or no IntersectionObserver support: fall back to always-visible
          // once connected, rather than a sticky bar that can never appear.
          this.isPastTrigger = true;
          this.applyVisibility();
          return;
        }

        this.triggerObserver = new IntersectionObserver(this.onTriggerIntersect, { threshold: 0 });
        this.triggerObserver.observe(this.triggerElement);
      }

      onTriggerIntersect(entries) {
        const entry = entries[entries.length - 1];
        // "Past" the trigger only once it has scrolled fully above the viewport - not merely
        // "not intersecting", which is also true before the page has scrolled down to it at all.
        this.isPastTrigger = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.applyVisibility();
      }

      onResize() {
        this.applyVisibility();
      }

      applyVisibility() {
        if (!this.sticky) return;

        if (window.innerWidth < 990) {
          this.sticky.classList.remove('is-visible');
          return;
        }

        this.sticky.classList.toggle('is-visible', Boolean(this.isPastTrigger));
      }

      updateImage(variant) {
        if (!this.image) return;

        const src80 = variant?.sticky_image_80 || this.productData?.sticky_image_80;
        const src120 = variant?.sticky_image_120 || this.productData?.sticky_image_120;
        const alt =
          variant?.sticky_image_alt ||
          variant?.variantTitle ||
          this.productData?.sticky_image_alt ||
          this.productData?.title ||
          '';

        if (!src120) return;

        this.image.src = src120;
        this.image.alt = alt;
        this.image.srcset = src80 ? `${src80} 80w, ${src120} 120w` : '';
      }

      formatMoney(cents) {
        if (
          window.Shopify &&
          typeof window.Shopify.formatMoney === 'function'
        ) {
          return window.Shopify.formatMoney(
            cents,
            window.theme?.moneyFormat || '{{ amount_with_comma_separator }} €'
          );
        }

        return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
      }

      getSelectedVariantId() {
        const variantInput =
          document.querySelector('form[action*="/cart/add"] [name="id"]') ||
          document.querySelector('product-form [name="id"]') ||
          document.querySelector('[name="id"]');

        return variantInput ? Number(variantInput.value) : null;
      }

      getVariantById(variantId) {
        if (!variantId || !this.productData?.variants) return null;
        return this.productData.variants.find((variant) => Number(variant.id) === Number(variantId)) || null;
      }

      observeVariantIdInput() {
        const variantInput =
          document.querySelector('form[action*="/cart/add"] [name="id"]') ||
          document.querySelector('product-form [name="id"]') ||
          document.querySelector('[name="id"]');

        if (!variantInput) return;

        this.variantObserver = new MutationObserver(() => {
          this.syncFromSelectedVariant();
        });

        this.variantObserver.observe(variantInput, {
          attributes: true,
          attributeFilter: ['value']
        });

        variantInput.addEventListener('input', () => this.syncFromSelectedVariant());
        variantInput.addEventListener('change', () => this.syncFromSelectedVariant());
      }

      onVariantChange(event) {
        const variant =
          event?.detail?.variant ||
          this.getVariantById(this.getSelectedVariantId());

        if (!variant) {
          this.syncFromSelectedVariant();
          return;
        }

        this.updateSticky(variant);
      }

      syncFromSelectedVariant() {
        const variantId = this.getSelectedVariantId();
        let variant = this.getVariantById(variantId);

        if (!variant && this.productData?.variants?.length) {
          variant = this.productData.variants[0];
        }

        if (!variant) return;
        this.updateSticky(variant);
      }

      updateSticky(variant) {
        if (!variant) return;

        this.updateImage(variant);

        if (this.prodName) {
          let productTitle = this.productData.title;

          if (this.productData.variants.length > 1 && variant?.variantTitle) {
            productTitle = variant.variantTitle;
          }

          this.prodName.textContent = productTitle;
        }

        if (this.currentPrice) {
          this.currentPrice.textContent = this.formatMoney(variant.price);
        }

        const hasDiscount =
          Number(variant.compare_at_price) > Number(variant.price);

        if (this.comparePrice) {
          if (hasDiscount) {
            this.comparePrice.textContent = this.formatMoney(variant.compare_at_price);
            this.comparePrice.classList.remove('hidden');
          } else {
            this.comparePrice.textContent = '';
            this.comparePrice.classList.add('hidden');
          }
        }

        if (this.discount) {
          if (hasDiscount) {
            const percent = Math.round(
              ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
            );
            this.discount.textContent = `${percent}% Rabatt`;
            this.discount.classList.remove('hidden');
          } else {
            this.discount.textContent = '';
            this.discount.classList.add('hidden');
          }
        }

        if (this.button) {
          this.button.disabled = !variant.available;
        }

        if (this.availability) {
          this.availability.classList.remove('is-low-stock', 'is-out-of-stock', 'hidden');

          const quantity = Number(variant.inventory_quantity || 0);
          const trackQuantity = variant.inventory_management;

          if (!variant.available) {
            this.availability.textContent = 'Ausverkauft';
            this.availability.classList.add('is-out-of-stock');
          } else if (trackQuantity && quantity > 0 && quantity <= this.lowStockThreshold) {
            this.availability.textContent = `Begrenzter Bestand: ${quantity} Stück übrig`;
            this.availability.classList.add('is-low-stock');
          } else {
            this.availability.textContent = '';
            this.availability.classList.add('hidden');
          }
        }
      }

      onButtonClick() {
        const mainAtcButton =
          document.querySelector('form[action*="/cart/add"] [type="submit"]') ||
          document.querySelector('product-form button[name="add"]') ||
          document.querySelector('[data-add-to-cart]');

        if (!mainAtcButton) return;

        mainAtcButton.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        window.setTimeout(() => {
          mainAtcButton.focus({ preventScroll: true });
        }, 450);
      }
    }
  );
}